import React, { useState, useEffect, useCallback } from "react";
import { Card } from "../../CoreUI";
import useResource from "../../../hooks/useResource";
import useTrack, { eventTypes } from "../../../hooks/useTrack";
import GET from "../../../utils/GET";
import { FaRegMinusSquare } from "react-icons/fa";
import {
	INTERMEDIATE_SURVEY_RESPONSE_URL,
	surveyResponseUrl,
} from "../../../conf";

import "./survey-card.css";

const customerEmail = GET("email");
const customerPhone = GET("phone");
const identifier = GET("identifier");
const cost = GET("cost");
const host = GET("host");
const utm = GET("utm");
const discountCode = GET("shopifyDisCo");
const shopifyOrderID = GET("shopifyOrderID");

const prepareSurveyReq = (customerID, survey, key, otherValue = false) => {
	if (!survey || typeof key !== "number") {
		return;
	}
	const userResponse = survey.options[key];
	const surveyResponseAddon = {
		question_id: survey.remote_id,
		response: userResponse.remote_id,
		customer_type: survey?.customer_type || null,
		utm: utm ? JSON.parse(utm) : null,
		discount_code: discountCode,
	};
	if (userResponse.text_input && otherValue) {
		surveyResponseAddon.text_response = otherValue;
	}
	if (customerEmail) {
		surveyResponseAddon.customer = customerEmail;
	} else if (customerPhone) {
		surveyResponseAddon.customer = customerPhone;
	} else {
		surveyResponseAddon.customer = customerID;
	}
	if (cost) {
		surveyResponseAddon.order_amount = cost;
	}
	return {
		...surveyResponse,
		body: JSON.stringify({
			...surveyResponse.body,
			...surveyResponseAddon,
		}),
	};
};

const modalSurveyOptionAppend = {
	icon_url: <FaRegMinusSquare />,
	icon: true,
	text: "I prefer not to answer, skip this",
	extra_text: "",
	modal_close: true,
};

const surveyResponse = {
	method: "POST",
	body: {
		order_id: identifier,
		referer_url: host ? host : "",
		shopify_order_id: shopifyOrderID,
	},
	headers: {
		"Content-Type": "application/json",
	},
};

const Survey = React.memo(
	({
		publisher = "you",
		survey,
		customerID = "",
		onClose,
		modal = false,
		inSurface = false,
		modalAnswer = -1,
		modalVisible = false,
		cardIdentifier = -1,
		active = false,
		preview = false,
	}) => {
		const inPreview = preview || GET("platform") || GET("full") || false;

		const [trackSurveyDisplay, setTrackSurveyDisplay] = useState(false);
		const [trackSurveyClick, setTrackSurveyClick] = useState(false);
		const [triggered, setTriggered] = useState(false);
		const track = useTrack();

		const [, trigger, , setOptions] = useResource(
			surveyResponseUrl,
			surveyResponse,
			false
		);

		const [, intermediateTrigger, , setIntermediateOptions] = useResource(
			INTERMEDIATE_SURVEY_RESPONSE_URL,
			surveyResponse,
			false
		);

		const handleIntermediateResponse = useCallback(
			(key) => {
				setIntermediateOptions(
					prepareSurveyReq(customerID, survey, key)
				);
				intermediateTrigger();
			},
			[customerID, survey, intermediateTrigger, setIntermediateOptions]
		);

		const handleSurveyResponse = useCallback(
			(key, otherValue = "", triggerEvent = true) => {
				if (preview) {
					return onClose(cardIdentifier, false, key);
				}
				if (modal && key === survey.options.length) {
					if (identifier) {
						const { question_text, remote_id } = survey;
						track(
							inSurface
								? eventTypes.WIDGET_SURFACE_SURVEY_SKIP
								: eventTypes.WIDGET_SURVEY_MODAL_SKIP,
							{
								question: question_text,
								question_id: remote_id,
								order_id: identifier,
								module_position: cardIdentifier,
								module_order: cardIdentifier,
								host: GET("host"),
							}
						);
					}
					return onClose(cardIdentifier, false, key);
				}
				if (surveyResponseUrl) {
					setOptions(
						prepareSurveyReq(customerID, survey, key, otherValue)
					);
					if (triggerEvent) {
						if (identifier && !trackSurveyClick) {
							const clickType = inSurface
								? eventTypes.WIDGET_SURFACE_SURVEY_CLICK
								: modal
								? eventTypes.WIDGET_SURVEY_MODAL_CLICK
								: eventTypes.WIDGET_SURVEY_CLICK;
							track(clickType, {
								response: survey.options[key].text,
								respone_id: survey.options[key].remote_id,
								response_order: key,
								module_position: cardIdentifier,
								module_order: cardIdentifier,
								input_text: otherValue,
							});
							setTrackSurveyClick(true);
						}
						trigger();
					}
					setTriggered(true);
					if (modal && !inSurface) {
						onClose(cardIdentifier, false, key);
					}
				}
			},
			[
				inSurface,
				preview,
				survey,
				modal,
				onClose,
				track,
				customerID,
				setOptions,
				trackSurveyClick,
				trigger,
				cardIdentifier,
			]
		);

		useEffect(() => {
			if (!triggered || !inSurface) {
				return;
			}
			onClose();
		}, [triggered, inSurface, onClose]);

		useEffect(() => {
			if (inPreview || inSurface) {
				return;
			}
			const { question_text, remote_id } = survey;
			if (
				identifier &&
				!trackSurveyDisplay &&
				!modal &&
				!modalVisible &&
				(modalAnswer < 0 || modalAnswer > survey.options.length)
			) {
				// TODO: COMMENT IF ADDING SURVEY FROM DESKTOP MODAL
				track(eventTypes.WIDGET_SURVEY_DISPLAY, {
					question: question_text,
					question_id: remote_id,
					order_id: identifier,
					module_position: cardIdentifier,
					module_order: cardIdentifier,
					host: GET("host"),
				});
				setTrackSurveyDisplay(true);
			} else if (identifier && !trackSurveyDisplay && modal && active) {
				track(eventTypes.WIDGET_SURVEY_MODAL_DISPLAY, {
					question: question_text,
					question_id: remote_id,
					order_id: identifier,
					module_position: cardIdentifier,
					module_order: cardIdentifier,
					host: GET("host"),
				});
				setTrackSurveyDisplay(true);
			} else if (!identifier && !trackSurveyDisplay && !modal) {
				track(eventTypes.WIDGET_SURVEY_NO_ORDER, {
					question: question_text,
					question_id: remote_id,
					order_id: identifier,
					module_position: cardIdentifier,
					module_order: cardIdentifier,
					host: GET("host"),
				});
				setTrackSurveyDisplay(true);
			}
		}, [
			inSurface,
			inPreview,
			modalVisible,
			modal,
			active,
			modalAnswer,
			survey,
			track,
			trackSurveyDisplay,
			setTrackSurveyDisplay,
			cardIdentifier,
		]);

		return (
			<Card
				options={
					modal
						? [...survey.options, modalSurveyOptionAppend]
						: survey.options
				}
				heading={survey.question_text}
				completion={`I found ${publisher}`}
				handleParent={handleSurveyResponse}
				isDefault={survey.is_default}
				modalAnswer={modalAnswer}
				handleIntermediateResponse={handleIntermediateResponse}
				style={modal ? { marginTop: "0px" } : {}}
			/>
		);
	}
);

export default Survey;
